import React from 'react';
import './AvailabilityImpactBadge.css';

const AVAILABILITY_IMPACT_LABELS = {
  yes: 'Impacted',
  no: 'No impact',
  pending: 'Pending',
};

const AVAILABILITY_IMPACT_TOOLTIPS = {
  yes: 'Clinical availability is impacted by this complaint',
  no: 'Device remains clinically available for this complaint',
  pending: 'Clinical availability impact not yet confirmed — set when work starts',
};

const DEVICE_AVAILABILITY_TOOLTIP = 'Active complaint is impacting clinical availability';

/**
 * @returns {'yes' | 'no' | 'pending' | null}
 */
export function getAvailabilityImpactStatus(complaint) {
  if (!complaint) return null;

  const status = complaint.status;
  const terminal = ['closed', 'cancelled'];
  const workStarted = ['in_progress', 'escalated_to_vendor'];

  if (!terminal.includes(status) && !workStarted.includes(status)) {
    if (!complaint.impacts_availability) return 'pending';
  }

  if (complaint.impacts_availability) return 'yes';
  return 'no';
}

export function getAvailabilityImpactLabel(complaint) {
  const impactStatus = getAvailabilityImpactStatus(complaint);
  if (!impactStatus) return '—';
  return AVAILABILITY_IMPACT_LABELS[impactStatus];
}

/**
 * Compact square marker for list/legend (matches Device List Impacted signal language).
 * yes = purple, pending = amber, no = green.
 */
export const AvailabilityImpactSignal = ({
  complaint,
  deviceHasImpact = false,
  impactStatus: impactStatusProp = null,
  showNeutral = false,
}) => {
  let impactStatus = impactStatusProp;

  if (!impactStatus) {
    if (complaint) {
      impactStatus = getAvailabilityImpactStatus(complaint);
    } else if (deviceHasImpact) {
      impactStatus = 'yes';
    }
  }

  if (!impactStatus) return null;
  if (impactStatus === 'no' && !showNeutral) return null;

  const label = AVAILABILITY_IMPACT_LABELS[impactStatus] || 'Availability';
  const detail = deviceHasImpact
    ? DEVICE_AVAILABILITY_TOOLTIP
    : AVAILABILITY_IMPACT_TOOLTIPS[impactStatus];

  return (
    <span
      className={`availability-impact-signal availability-impact-signal--${impactStatus}`}
      title={detail}
      aria-label={`Clinical availability: ${label}`}
      role="img"
    />
  );
};

export const AvailabilityImpactBadge = ({
  complaint,
  deviceHasImpact = false,
  size = 'medium',
  showNeutral = false,
}) => {
  let impactStatus = null;

  if (complaint) {
    impactStatus = getAvailabilityImpactStatus(complaint);
  } else if (deviceHasImpact) {
    impactStatus = 'yes';
  }

  if (!impactStatus) return null;

  if (size === 'small' && impactStatus === 'no' && !showNeutral) {
    return null;
  }

  const label = deviceHasImpact
    ? 'Availability impacted'
    : AVAILABILITY_IMPACT_LABELS[impactStatus];
  const badgeTitle = deviceHasImpact
    ? DEVICE_AVAILABILITY_TOOLTIP
    : AVAILABILITY_IMPACT_TOOLTIPS[impactStatus];
  const deviceClass = deviceHasImpact ? ' availability-impact-note--device' : '';

  return (
    <span
      className={`availability-impact-note availability-impact-note--${impactStatus} availability-impact-note-${size}${deviceClass}`}
      title={badgeTitle}
      role="status"
      aria-label={`Clinical availability: ${label}`}
    >
      <span className="availability-impact-dot" aria-hidden="true" />
      <span className="availability-impact-label">{label}</span>
    </span>
  );
};
